/**
 * The alert section of the Defender board (L1): the raw volume behind the
 * incidents, and what it is made of.
 *
 * Alert volume is deliberately **not** toned as good or bad. A high alert count
 * is not a finding — it is the base every mix below is a share of, and a board
 * that painted it red would be scoring Defender for doing its job. What is
 * toned is severity, which is a status scale (§3.2), and the open figure, which
 * is work outstanding.
 *
 * Alert *titles* are absent from the payload by design: a title is a sentence
 * Defender composed about a person or a device ("Suspicious inbox forwarding
 * rule for …"). So there is no alert list at any altitude here, and the deepest
 * drill is a chart's table twin over its own buckets.
 *
 * Five of the seven breakdowns are floored at k≥5, with sub-floor cohorts folded
 * into one unlabelled `Other` row. Each panel states its own fold rather than
 * shipping a quietly shorter list — a silent fold is the §5 defect.
 *
 * `determination` is one of the floored five, and it is floored for exactly the
 * reason `classification` is: no identifier is published either way, but "one
 * alert, notMalicious, last Tuesday" plus someone who knows the estate is a
 * narrowing step, so a determination bucket of one folds rather than displays.
 */

import { Statistic } from "@norican/react";

import type { DefenderAlerts } from "@/lib/api";

import { DefenderErrorsAlert } from "./access-panel";
import { BreakdownPanel } from "./breakdown-panel";
import { SectionHeading } from "./panel-card";
import { ALERTS_ANCHOR, TWIN_IDS } from "./params";
import { formatCount, formatWindow, problemTone } from "./status";
import { TrendPanel } from "./trend-panel";

interface AlertsSectionProps {
  alerts: DefenderAlerts;
}

export function AlertsSection({ alerts }: AlertsSectionProps) {
  const windowLabel = formatWindow(alerts.window);
  const staleAfterMs = alerts.cache_ttl_sec * 2000;
  const since = alerts.collected_at;

  return (
    <section id={ALERTS_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      <SectionHeading
        title="Alerts"
        subtitle={`Detection volume and its mix, ${windowLabel}. Volume is the base the mixes below are shares of, not a verdict — and no alert title is published, because a title is a sentence Defender wrote about a person or a device.`}
      />

      <DefenderErrorsAlert errors={alerts.errors} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Statistic
          label="Alerts raised"
          value={formatCount(alerts.alerts_total)}
          tone={alerts.alerts_total == null ? "neutral" : "accent"}
          accent
          caption={
            alerts.alerts_total == null
              ? "The read failed — unknown, not zero"
              : alerts.truncated
                ? `At least this many, ${windowLabel} — paging hit NOVA's cap`
                : `Created ${windowLabel}. Volume, not a verdict.`
          }
        />
        <Statistic
          label="Still open"
          value={formatCount(alerts.open_alerts)}
          tone={problemTone(alerts.open_alerts)}
          accent
          caption={
            alerts.open_alerts == null
              ? "The read failed — unknown, not zero"
              : alerts.open_alerts === 0
                ? "Every alert in the window has been resolved — a measured zero"
                : "Unresolved of the alerts raised in the window"
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BreakdownPanel
          title="Alerts by severity"
          breakdown={alerts.by_severity}
          form="donut"
          noun="alerts"
          palette="severity"
          ariaLabel={`Alerts by severity, ${windowLabel}`}
          twinId={TWIN_IDS.alertSeverity}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={alerts.truncated}
          caption={windowLabel}
          totalLabel="Alerts"
          emptyTitle="No alerts to break down"
          emptyDescription="The severity read answered and found no alerts in the window. A measured zero, not a refused read."
        />
        <BreakdownPanel
          title="Alerts by status"
          breakdown={alerts.by_status}
          noun="alerts"
          palette="identity"
          ariaLabel={`Alerts by status, ${windowLabel}`}
          twinId={TWIN_IDS.alertStatus}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={alerts.truncated}
          caption={windowLabel}
          emptyTitle="No alerts to break down by status"
          emptyDescription="The status read answered and found no alerts in the window."
        />
      </div>

      <TrendPanel
        title="Alerts raised per day"
        points={alerts.volume_trend}
        noun="alerts"
        ariaLabel={`Alerts created per UTC day, ${windowLabel}`}
        twinId={TWIN_IDS.alertTrend}
        since={since}
        staleAfterMs={staleAfterMs}
        truncated={alerts.truncated}
        caption={windowLabel}
        emptyTitle="No alert was raised in this window"
        emptyDescription="The window read answered and held no alerts. A measured absence — nothing is drawn rather than a flat line along the axis."
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BreakdownPanel
          title="Alerts by analyst determination"
          breakdown={alerts.by_determination}
          noun="alerts"
          palette="identity"
          ariaLabel={`Alerts by analyst determination, ${windowLabel}`}
          twinId={TWIN_IDS.alertDetermination}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={alerts.truncated}
          // What triage concluded, which is the closest this board gets to
          // measuring alert-tuning debt: determinations that keep coming back
          // not-malicious are analyst hours spent on a detection that needs work.
          caption="what triage concluded · aggregated behind a k-anonymity floor"
          emptyTitle="Nothing has a determination"
          emptyDescription="No alert in the window has been given a determination yet. That is triage that has not finished, not a set of alerts that turned out to be nothing."
        />
        <BreakdownPanel
          title="Alerts by Defender product"
          breakdown={alerts.by_service_source}
          noun="alerts"
          palette="identity"
          ariaLabel={`Alerts by the Defender product that raised them, ${windowLabel}`}
          twinId={TWIN_IDS.alertServiceSource}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={alerts.truncated}
          caption="aggregated behind a k-anonymity floor"
          emptyTitle="No product attribution to show"
          emptyDescription="No alert in the window named the Defender product that raised it."
        />
        <BreakdownPanel
          title="Alerts by detection source"
          breakdown={alerts.by_detection_source}
          noun="alerts"
          palette="identity"
          ariaLabel={`Alerts by detection source, ${windowLabel}`}
          twinId={TWIN_IDS.alertDetectionSource}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={alerts.truncated}
          caption="aggregated behind a k-anonymity floor"
          emptyTitle="No detection source to show"
          emptyDescription="No alert in the window named the detector that produced it."
        />
        <BreakdownPanel
          title="Alerts by category"
          breakdown={alerts.by_category}
          noun="alerts"
          palette="identity"
          ariaLabel={`Alerts by category, ${windowLabel}`}
          twinId={TWIN_IDS.alertCategory}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={alerts.truncated}
          caption="aggregated behind a k-anonymity floor"
          emptyTitle="No category to show"
          emptyDescription="No alert in the window carried a category. Categories are Microsoft's vocabulary and change without a deploy, so an unfamiliar one is shown as its own bucket rather than folded away."
        />
        <BreakdownPanel
          title="Alerts by MITRE technique"
          breakdown={alerts.by_technique}
          noun="alerts"
          palette="identity"
          ariaLabel={`Alerts by MITRE ATT&CK technique id, ${windowLabel}`}
          twinId={TWIN_IDS.alertTechnique}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={alerts.truncated}
          // One alert can carry several techniques, so these buckets sum above
          // the alert total. Said here rather than left to be discovered from a
          // share that does not add to 100%.
          caption="one alert can carry several techniques, so these sum above the alert total · aggregated behind a k-anonymity floor"
          emptyTitle="No technique attribution to show"
          emptyDescription="No alert in the window was mapped to a MITRE ATT&CK technique."
        />
      </div>
    </section>
  );
}
